import React, { forwardRef } from "react";
import QRCode from "qrcode.react";
import { formatCurrency } from "../../utils/currency";

// Local helper for currency formatting
const localFormatCurrency = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 2,
  }).format(value || 0);
};

// --- UPDATED HARDCODED MOCK DATA ---
const MOCK_ORDER = {
  order_number: "ORD-99210",
  waiter: "Rahul Sharma",
  created_at: "11/01/2026, 02:45 PM",
  order_type: "Dine In",
  subtotal: 560.0,
  discount: 50.0,
  tax: 25.5,
  total: 535.5,
  storeInfo: {
    restaurantName: "RAJENDRA HOTEL & BAR",
    address: "123, MG Road, Mumbai, Maharashtra",
    logoUrl: "https://img.logoipsum.com/285.svg",
    vpa: "rajendrahotel@upi",
    footerNote: "Thank you for dining with us!",
    contact: "+91 98765 43210",
    gstin: "27AAAAA0000A1Z5",
  },
  dine: {
    table: { table_name: "T-04" },
    dine_area_name: "First Floor",
  },
  items: [
    {
      variant_name: "Paneer Butter Masala",
      quantity: 1,
      current_price: { final_price: 280.0 },
      addons: [{ name: "Extra Butter", price: 30.0, quantity: 2 }],
      notes: ["Extra Spicy"],
    },
    {
      variant_name: "Butter Naan",
      quantity: 3,
      current_price: { final_price: 45.0 },
      addons: [],
      notes: [],
    },
    {
      variant_name: "Masala Papad",
      quantity: 2,
      current_price: { final_price: 35.0 },
      addons: [{ name: "Cheese Topping", price: 15.0, quantity: 1 }],
      notes: ["Double Roasted"],
    },
  ],
};

const PrintReceipt = forwardRef(({ width = "80mm" }, ref) => {
  const order = MOCK_ORDER;

  return (
    <div
      ref={ref}
      style={{
        width: width,
        padding: "8px",
        fontFamily: "'Courier New', Courier, monospace",
        fontSize: "12px",
        backgroundColor: "#fff",
        color: "#000",
        lineHeight: "1.4",
        margin: "0 auto",
      }}
    >
      {order && (
        <div style={{ width: "100%" }}>
          {/* 1. HEADER */}
          <div style={{ textAlign: "center", marginBottom: "5px" }}>
            <div
              style={{
                fontWeight: "bold",
                fontSize: "16px",
                textTransform: "uppercase",
              }}
            >
              {order.storeInfo.restaurantName}
            </div>
            <div style={{ fontSize: "10px" }}>
              {order.storeInfo.address}
            </div>
            <div style={{ fontSize: "10px" }}>
              GSTIN: {order.storeInfo.gstin}
            </div>
          </div>

          <div style={{ borderBottom: "1px dashed #000", margin: "8px 0" }} />

          {/* 2. ORDER INFO */}
          <table
            style={{
              width: "100%",
              fontSize: "11px",
              borderCollapse: "collapse",
            }}
          >
            <tbody>
              <tr>
                <td style={{ textAlign: "left" }}>
                  <b>Order:</b> #{order.order_number}
                </td>
                <td style={{ textAlign: "right" }}>
                  <b>Table:</b> {order.dine.table.table_name}
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: "left" }}>
                  <b>Type:</b> {order.order_type}
                </td>
                <td style={{ textAlign: "right" }}>
                  <b>Date:</b> {order.created_at.split(",")[0]}
                </td>
              </tr>
              <tr>
                <td colSpan="2" style={{ textAlign: "left" }}>
                  <b>Waiter:</b> {order.waiter}
                </td>
              </tr>
            </tbody>
          </table>

          <div style={{ borderBottom: "1px dashed #000", margin: "8px 0" }} />

          {/* 3. ITEMS TABLE (FIXED ALIGNMENT) */}
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              tableLayout: "fixed",
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid #000" }}>
                <th style={{ textAlign: "left", width: "60%" }}>ITEM</th>
                <th style={{ textAlign: "center", width: "10%" }}>QTY</th>
                <th style={{ textAlign: "right", width: "25%" }}>RATE</th>
                <th style={{ textAlign: "right", width: "25%" }}>AMT</th>
              </tr>
            </thead>
            <tbody>
              {order.items.map((item, i) => (
                <React.Fragment key={i}>
                  <tr>
                    <td
                      style={{
                        paddingTop: "5px",
                        verticalAlign: "top",
                        fontWeight: "bold",
                        wordWrap: "break-word",
                      }}
                    >
                      {item.variant_name}
                    </td>
                    <td
                      style={{
                        paddingTop: "5px",
                        verticalAlign: "top",
                        textAlign: "center",
                      }}
                    >
                      {item.quantity}
                    </td>
                    <td
                      style={{
                        paddingTop: "5px",
                        verticalAlign: "top",
                        textAlign: "right",
                      }}
                    >
                      {formatCurrency(item.current_price.final_price)}
                    </td>
                    <td
                      style={{
                        paddingTop: "5px",
                        verticalAlign: "top",
                        textAlign: "right",
                      }}
                    >
                      {formatCurrency(
                        item.current_price.final_price * item.quantity
                      )}
                    </td>
                  </tr>
                  {/* Addons */}
                  {item.addons?.map((addon, aIdx) => (
                    <tr key={aIdx} style={{ fontSize: "10px", color: "#333" }}>
                      <td style={{ paddingLeft: "8px" }}>
                        + {addon.name}{" "}
                        {addon.quantity > 1 ? `(x${addon.quantity})` : ""}
                      </td>
                      <td></td>
                      <td style={{ textAlign: "right" }}>
                        {localFormatCurrency(addon.price * addon.quantity)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>

          <div style={{ borderBottom: "1px dashed #000", margin: "8px 0" }} />

          {/* 4. TOTALS SECTION */}
          <table style={{ width: "100%", lineHeight: "1.6" }}>
            <tbody>
              <tr>
                <td style={{ textAlign: "left" }}>Subtotal</td>
                <td style={{ textAlign: "right" }}>
                  {localFormatCurrency(order.subtotal)}
                </td>
              </tr>
              {order.discount > 0 && (
                <tr>
                  <td style={{ textAlign: "left" }}>Discount</td>
                  <td style={{ textAlign: "right" }}>
                    -{localFormatCurrency(order.discount)}
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ textAlign: "left", fontSize: "11px" }}>
                  CGST (2.5%)
                </td>
                <td style={{ textAlign: "right", fontSize: "11px" }}>
                  {localFormatCurrency(order.tax / 2)}
                </td>
              </tr>
              <tr>
                <td style={{ textAlign: "left", fontSize: "11px" }}>
                  SGST (2.5%)
                </td>
                <td style={{ textAlign: "right", fontSize: "11px" }}>
                  {localFormatCurrency(order.tax / 2)}
                </td>
              </tr>
              <tr style={{ fontWeight: "bold", fontSize: "15px" }}>
                <td
                  style={{
                    textAlign: "left",
                    borderTop: "1px solid #000",
                    paddingTop: "5px",
                  }}
                >
                  GRAND TOTAL
                </td>
                <td
                  style={{
                    textAlign: "right",
                    borderTop: "1px solid #000",
                    paddingTop: "5px",
                  }}
                >
                  {localFormatCurrency(order.total)}
                </td>
              </tr>
            </tbody>
          </table>

          <div style={{ borderBottom: "1px dashed #000", margin: "10px 0" }} />

          {/* 5. QR CODE & FOOTER */}
          <div style={{ textAlign: "center" }}>
            <QRCode
              value={`upi://pay?pa=${
                order.storeInfo.vpa
              }&pn=${order.storeInfo.restaurantName.replace(/\s/g, "%20")}&am=${
                order.total
              }&cu=INR`}
              size={80}
              renderAs="svg"
            />
            <div
              style={{ fontSize: "10px", fontWeight: "bold", marginTop: "4px" }}
            >
              SCAN TO PAY
            </div>

            <div style={{ marginTop: "10px", fontSize: "10px" }}>
              {order.storeInfo.footerNote} <br />
              {order.storeInfo.contact}
            </div>
            <div style={{ fontSize: "9px", marginTop: "10px", color: "#666" }}>
              --- YourPOS v1.0 ---
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default PrintReceipt;
